import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Typography, Fade, Button } from '@mui/material';
import MassActionMailEditor from './MassActionMailEditor';
import MassActionSMSEditor from './MassActionSMSEditor';
import DiffusionTypeButton from './DiffusionTypeButton';
import MessageActionAppBar from './MessageActionAppBar';
import MassActionNotifyEditor from './MassActionNotifyEditor';
import MassActionXplNotifyEditor from './MassActionXplNotifyEditor';
import contentEditorUtils from 'src/components/common/contentEditor/utils/contentEditorUtils';
import paramSelectors from 'src/selectors/parameters';
import SmsContentLabel from './SmsContentLabel';
import ActionType from 'src/enums/actionType';
import { CONTENT_SELECTOR } from '../MassActionEditor';
import massActionUtils from 'src/components/massAction/state/utils';
import { useMassActionState } from '../../MassActionStateContext';

function PageContent({
  actionType,
  fields,
  content,
  object,
  setStep,
  goToStep,
  actionId,
  isWritable,
}) {
  const { t } = useTranslation();
  const [, setMassActionState] = useMassActionState();
  const stopSmsText = useSelector(state => state.app.config.stopSmsText);

  const handleChange = useCallback(() => {
    setMassActionState(state => ({
      ...state,
      contentHasChanges: true,
      contentValidity: massActionUtils.getContentValidity({
        actionType,
        object: state.objectEditor && state.objectEditor.getData(),
        content: state.editor.getData(),
        min: state.selectedDiffusionType === 'advertising' ? stopSmsText.length : 0,
      }),
    }));
  }, [actionType, setMassActionState, stopSmsText.length]);

  const onEditorLoaded = editor => setMassActionState(prevState => ({ ...prevState, editor }));

  const onObjectEditorLoaded = objectEditor =>
    setMassActionState(prevState => ({ ...prevState, objectEditor }));

  const clearCurrentCampaignAction = () =>
    setMassActionState(prevState => ({ ...prevState, currentCampaignAction: null }));

  const { headerStoredfileId, footerStoredfileId } = useSelector(paramSelectors.getCurrentParams);

  const params = {
    headerFileId: headerStoredfileId || null,
    footerFileId: footerStoredfileId || null,
  };

  return (
    <div
      style={{
        display: 'flex',
        height: '100%',
        flexDirection: 'column',
        overflow: 'hidden',
        textAlign: 'left',
      }}
    >
      <MessageActionAppBar
        setStep={setStep}
        goToStep={goToStep}
        actionType={actionType}
        fields={fields}
        actionId={actionId}
        isWritable={isWritable}
      />
      <Fade in timeout={500}>
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            padding: '8px',
          }}
        >
          {![ActionType.MEMBER_NOTIFICATION.id].includes(actionType) && (
            <>
              <Typography
                color="textSecondary"
                style={{ margin: '5px 10px', display: 'inline' }}
                variant="subtitle2"
                noWrap
                component="div"
              >
                {t('smartmessaging.massAction.diffusionTypeLabel')}
              </Typography>
              <div style={{ display: 'inline' }}>
                <DiffusionTypeButton actionType={actionType} />
              </div>
            </>
          )}
          {actionType === ActionType.SMS.id && (
            <div style={{ width: '320px' }}>
              <div>
                <SmsContentLabel />
              </div>
              <MassActionSMSEditor
                isWritable={isWritable}
                fields={fields}
                content={contentEditorUtils.smsFreemarkerToHtml(
                  content,
                  fields.fieldsModels,
                  params
                )}
                onChange={handleChange}
                onEditorLoaded={onEditorLoaded}
              />
            </div>
          )}
          {actionType === ActionType.EMAIL.id && (
            <MassActionMailEditor
              onObjectEditorLoaded={onObjectEditorLoaded}
              isWritable={isWritable}
              onEditorLoaded={onEditorLoaded}
              onChange={handleChange}
              fields={fields}
              content={contentEditorUtils.freemarkerToHtml(content, fields.fieldsModels, params)}
              object={contentEditorUtils.freemarkerToHtml(object, fields.fieldsModels, params)}
            />
          )}
          {actionType === ActionType.MEMBER_NOTIFICATION.id && (
            <MassActionNotifyEditor
              onObjectEditorLoaded={onObjectEditorLoaded}
              isWritable={isWritable}
              onEditorLoaded={onEditorLoaded}
              onChange={handleChange}
              fields={fields}
              content={contentEditorUtils.freemarkerToHtml(content, fields.fieldsModels, params)}
              object={contentEditorUtils.freemarkerToHtml(object, fields.fieldsModels, params)}
            />
          )}
          {actionType === ActionType.PUSH_XPLORACTIVE.id && (
            <MassActionXplNotifyEditor
              onObjectEditorLoaded={onObjectEditorLoaded}
              isWritable={isWritable}
              onEditorLoaded={onEditorLoaded}
              onChange={handleChange}
              fields={fields}
              content={contentEditorUtils.freemarkerToHtml(content, fields.fieldsModels, params)}
              object={contentEditorUtils.freemarkerToHtml(object, fields.fieldsModels, params)}
            />
          )}
        </div>
      </Fade>
      <div style={{ textAlign: 'right', padding: '8px' }}>
        <Button
          variant="text"
          color="primary"
          style={{ margin: '2px' }}
          onClick={e => {
            e.preventDefault();
            clearCurrentCampaignAction();
            goToStep(CONTENT_SELECTOR);
          }}
        >
          {t('smartmessaging.buttonLabel.previous')}
        </Button>
      </div>
    </div>
  );
}

PageContent.propTypes = {
  actionType: PropTypes.number.isRequired,
  content: PropTypes.string.isRequired,
  object: PropTypes.string,
  fields: PropTypes.object,
  goToStep: PropTypes.func.isRequired,
  setStep: PropTypes.func.isRequired,
  actionId: PropTypes.number,
  isWritable: PropTypes.bool.isRequired,
};

PageContent.defaultProps = { object: '', actionId: null, fields: null };

const MessageContentEditor = ({
  actionType,
  fields,
  content,
  object,
  goToStep,
  setStep,
  actionId,
  isWritable,
}) => (
  <PageContent
    isWritable={isWritable}
    goToStep={goToStep}
    setStep={setStep}
    fields={fields}
    content={content}
    object={object}
    actionType={actionType}
    actionId={actionId}
  />
);

MessageContentEditor.propTypes = {
  actionType: PropTypes.number.isRequired,
  actionId: PropTypes.number,
  content: PropTypes.string.isRequired,
  object: PropTypes.string,
  fields: PropTypes.object,
  goToStep: PropTypes.func.isRequired,
  setStep: PropTypes.func.isRequired,
  isWritable: PropTypes.bool.isRequired,
};

MessageContentEditor.defaultProps = {
  object: '',
  actionId: null,
  fields: null,
};

export default MessageContentEditor;
